import { CircleDollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { DropdownMenuItem } from '~/components/ui/dropdown-menu';
import { Switch } from '~/components/ui/switch';
import { useCostApprovalSettings } from '~/features/platform/hooks/use-cost-approval-settings';
import {
  useMyPreferences,
  useUpdateMyPreferences,
} from '~/features/settings/hooks/use-my-preferences';
import { isInternalUser } from '~/hooks/useCanSeeCost';
import { useAuthStore } from '~/store/auth.store';
import { cn } from '~/utils/cn';

/**
 * The user's own "Show cost" switch for the account (avatar) menu, saved per user in
 * agentic-config — hides cost amounts for them alone, e.g. while demoing. lily.ai users (and super
 * admins) only: nobody else gets the option, and customer cost approvers keep seeing cost. Locked
 * off while a super admin hides cost for everyone (Platform → Cost approval). Display only: the
 * cost gate and approvals keep running.
 */
export function ShowCostMenuItem({ testId = 'account-menu-show-cost' }: { testId?: string }) {
  const internal = useAuthStore((s) => isInternalUser(s.user));
  const global = useCostApprovalSettings({ enabled: internal });
  const mine = useMyPreferences({ enabled: internal });
  const update = useUpdateMyPreferences();
  if (!internal) return null;

  const hiddenByAdmin = global.data?.showCostInUi === false;
  const checked = !hiddenByAdmin && (mine.data?.showCostInUi ?? true);
  // Locked while loading, or when the read failed (nothing to toggle against yet).
  const locked = hiddenByAdmin || mine.isPending || mine.isError;

  const toggle = () => {
    update.mutate(
      { showCostInUi: !checked },
      {
        onError: () => {
          toast.error('Could not save your cost display setting');
        },
      },
    );
  };

  return (
    <>
      <DropdownMenuItem
        data-testid={`${testId}-item`}
        disabled={locked}
        className={cn(
          'justify-between gap-3 rounded-lg px-3 py-2.5',
          locked ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
        // Keep the menu open so the switch visibly flips.
        onSelect={(e) => {
          e.preventDefault();
          if (!update.isPending) toggle();
        }}
      >
        <span className="flex items-center gap-3">
          <CircleDollarSign aria-hidden="true" className="text-nav-icon h-4 w-4" />
          <span className="text-sm">Show cost</span>
        </span>
        {update.isPending ? (
          <Loader2
            data-testid={`${testId}-saving`}
            aria-label="Saving"
            className="text-muted-foreground h-4 w-4 animate-spin"
          />
        ) : (
          <Switch
            data-testid={`${testId}-switch`}
            checked={checked}
            disabled={locked}
            tabIndex={-1}
            aria-label="Show cost"
            className="pointer-events-none"
          />
        )}
      </DropdownMenuItem>
      {hiddenByAdmin ? (
        <p
          data-testid={`${testId}-hidden-by-admin`}
          className="text-muted-foreground px-3 pb-1.5 text-xs"
        >
          Hidden for everyone by an admin
        </p>
      ) : null}
    </>
  );
}
